// src/components/Verify.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  styled,
  alpha,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import previewImage from '../assets/preview.png';
import logo from '../assets/logo.png';

// ─────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────
const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 40%',
  padding: theme.spacing(6),
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    flex: '1 1 auto',
    padding: theme.spacing(4),
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  backgroundColor: '#f7f8fa',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const BrandHeader = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  marginBottom: theme.spacing(4),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 28,
  marginBottom: theme.spacing(1),
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: '#6b7280',
  marginBottom: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
  '& input:-webkit-autofill': {
    WebkitBoxShadow: `0 0 0 1000px #fff inset !important`,
    boxShadow: `0 0 0 1000px #fff inset !important`,
  },
}));

const BlackButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: 12,
  padding: theme.spacing(1.5),
  fontWeight: 600,
  backgroundColor: '#000',
  color: '#fff',
  '&:hover': {
    backgroundColor: alpha('#000', 0.85),
  },
}));

// ─────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────
export default function Verify() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [vendorId, setVendorId] = useState(null);
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyVendor = async () => {
      try {
        const res = await fetch(`/api/auth/verify/${token}`);
        const data = await res.json();

        if (data.error) {
          // go straight to login
          window.location.href = '/login';
          return;
        }

        if (data.ok === false) {
          // link expired → resent email
          setResent(true);
          setLoading(false);
          return;
        }

        if (data.ok === true) {
          setVendorId(data.vendorId);
          setLoading(false);
        }
      } catch (error) {
        window.location.href = '/login';
      }
    };

    verifyVendor();
  }, [token]);

  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    if (!passwordsMatch) return;
    try {
      const res = await fetch(`/api/auth/complete-registration/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendorId, password }),
      });
      const data = await res.json();

      if (data.error) {
        window.location.href = '/login';
        return;
      }

      localStorage.setItem('token', data.token);
      // A membership payment is required before the account is usable.
      if (data.needsSubscription) {
        window.location.href = `/subscribe/${data.vendorId}`;
      } else {
        window.location.href = `/dashboard/${data.vendorId}`;
      }
    } catch (error) {
      window.location.href = '/login';
    }
  };

  return (
    <Container>
      <LeftPanel>
        <Box component="a" href="/" sx={{ display: 'inline-block', mb: 4, textDecoration: 'none' }}>
          <Box component="img" src={logo} alt="Rewarding" sx={{ height: 32 }} />
        </Box>

        {loading ? (
          <Title>Verifying...</Title>
        ) : resent ? (
          <>
            <Title>Verification Link Expired</Title>
            <SubTitle>
              A new verification email has been sent. Please check your inbox.
            </SubTitle>
            <BlackButton
              fullWidth
              onClick={() => (window.location.href = '/login')}
            >
              Back to Login
            </BlackButton>
          </>
        ) : (vendorId) ? (
          <>
            <Title>Verification Successful!</Title>
            <SubTitle>Set your password to access your Rewarding account.</SubTitle>
            <form onSubmit={handleCompleteRegistration}>
              <StyledTextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <StyledTextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!confirmPassword && password !== confirmPassword}
                helperText={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword((v) => !v)} edge="end" size="small">
                        {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <BlackButton type="submit" fullWidth disabled={!passwordsMatch} sx={{ mt: 2, mb: 2 }}>
                Complete Registration
              </BlackButton>
            </form>
          </>
        ) : null}
      </LeftPanel>

      <RightPanel sx={{ flexDirection: 'column', rowGap: 4 }}>
        <Box sx={{ marginRight: '80%', display: 'flex', columnGap: 0.8 }}>
          <Box sx={{ borderRadius: '100%', width: 10, height: 10, backgroundColor: '#FF5F57' }} />
          <Box sx={{ borderRadius: '100%', width: 10, height: 10, backgroundColor: '#FFBD2E' }} />
          <Box sx={{ borderRadius: '100%', width: 10, height: 10, backgroundColor: '#27C93F' }} />
        </Box>
        <Box
          component="img"
          src={previewImage}
          alt="Website Preview"
          sx={{ width: '130%', height: 'auto', marginRight: '-45%' }}
        />
      </RightPanel>
    </Container>
  );
}
